import React from "react";
import { MONTH_NAMES, MONTH_NEGS, STANDARD_DOOMSDAY_DATES } from "./doomsday";

function Method({ details, monthMethod, setMonthMethod }) {
  return (
    <div id="method">
      <h1>The method</h1>

      <p className="lead">
        Learn the mental model in four short steps. The numbers below update
        live with your selected date.
      </p>
      <div className="summary-grid">
        <div className="summary-card">
          <h3>Step 1</h3>
          <p>Century anchor</p>
        </div>
        <div className="summary-card">
          <h3>Step 2</h3>
          <p>Year doomsday</p>
        </div>
        <div className="summary-card">
          <h3>Step 3</h3>
          <p>Month method</p>
        </div>
        <div className="summary-card">
          <h3>Step 4</h3>
          <p>Leap adjustment</p>
        </div>
      </div>

      <div className="legend">
        <p>Weekday index reference</p>
        <table>
          <thead>
            <tr>
              <th>Index</th>
              <th>Day</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>0</td>
              <td>Sunday</td>
            </tr>
            <tr>
              <td>1</td>
              <td>Monday</td>
            </tr>
            <tr>
              <td>2</td>
              <td>Tuesday</td>
            </tr>
            <tr>
              <td>3</td>
              <td>Wednesday</td>
            </tr>
            <tr>
              <td>4</td>
              <td>Thursday</td>
            </tr>
            <tr>
              <td>5</td>
              <td>Friday</td>
            </tr>
            <tr>
              <td>6</td>
              <td>Saturday</td>
            </tr>
          </tbody>
        </table>
      </div>

      <details className="accordion" open>
        <summary>Step 1: Century anchor</summary>
        <div className="accordion-content">
          <p className="step-intro">Formula</p>
          <pre>
            <code>(5 × (c % 4)) + 2</code>
          </pre>
          <p>Century (c): {details.century}</p>
          <p>(c % 4): {details.century % 4}</p>
          <p>5 × (c % 4): {5 * (details.century % 4)}</p>
          <p>Mod 7: {(5 * (details.century % 4)) % 7}</p>
          <p>Add 2: {details.anchorRaw}</p>
          {details.anchorRaw > 6 ? (
            <p>Over 6? Subtract 7 → {details.anchor}</p>
          ) : (
            <p>Anchor = {details.anchor}</p>
          )}
        </div>
      </details>

      <details className="accordion" open>
        <summary>Step 2: Year doomsday (odd + 11)</summary>
        <div className="accordion-content">
          <p className="step-intro">Last two digits: {details.decade}</p>
          <pre>
            <code>odd? +11 → /2 → odd? +11 → mod 7</code>
          </pre>
          <p>After first odd check: {details.d}</p>
          <p>Divide by 2: {details.d2}</p>
          <p>After second odd check: {details.d3}</p>
          <p>Mod 7: {details.d4}</p>
          <p>Century anchor + (7 - mod): {details.anchor + 7 - details.d4}</p>
          <p>
            Doomsday index: {details.doomsday} ({details.doomsdayName})
          </p>
        </div>
      </details>

      <details className="accordion" open>
        <summary>Step 3: Month method</summary>
        <div className="accordion-content">
          <div className="toggle-row">
            <button
              className={
                monthMethod === "monthNeg"
                  ? "toggle active"
                  : "toggle"
              }
              onClick={() => setMonthMethod("monthNeg")}
            >
              Month neg (my shortcut)
            </button>
            <button
              className={
                monthMethod === "standard" ? "toggle active" : "toggle"
              }
              onClick={() => setMonthMethod("standard")}
            >
              Standard doomsday dates
            </button>
          </div>

          {monthMethod === "monthNeg" ? (
            <>
              <p className="step-intro">
                My personal shortcut: memorize one neg value per month.
              </p>
              <pre>
                <code>(day + monthNeg) % 7</code>
              </pre>
              <p>
                Month neg for {details.monthName}: {details.monthNeg}
              </p>
              <p>Day + neg: {details.day + details.monthNeg}</p>
              <p>Offset mod 7: {details.monthNegOffset}</p>
              <p>Final index: {details.weekdayIndex}</p>
              <p className="tip">
                Standard approach: memorize fixed doomsday dates (4/4, 6/6,
                8/8, 10/10, 12/12, etc.). Different schemes are fine as long as
                offsets stay consistent mod 7.
              </p>
            </>
          ) : (
            <>
              <p className="step-intro">
                Standard method: compare against the month’s doomsday date.
              </p>
              <pre>
                <code>(day - doomsdayDate) % 7</code>
              </pre>
              <p>
                Doomsday date for {details.monthName}: {details.standardBase}
              </p>
              <p>Offset mod 7: {details.standardOffset}</p>
              <p>Final index: {details.weekdayIndex}</p>
            </>
          )}

          <h3>Month neg table</h3>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Neg</th>
              </tr>
            </thead>
            <tbody>
              {MONTH_NAMES.map((label, index) => {
                const monthNumber = index + 1;
                return (
                  <tr key={label}>
                    <td>
                      {monthNumber} {label}
                    </td>
                    <td>{MONTH_NEGS[monthNumber]}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="tip">
            Standard doomsday dates:{" "}
            {Object.entries(STANDARD_DOOMSDAY_DATES)
              .map(([monthNumber, day]) => `${monthNumber}/${day}`)
              .join(", ")}
          </p>
        </div>
      </details>

      <details className="accordion" open>
        <summary>Step 4: Leap year adjustment</summary>
        <div className="accordion-content">
          <pre>
            <code>Leap year? subtract 1 for Jan/Feb</code>
          </pre>
          <p>
            {details.year} is{" "}
            {details.leapYear ? "a leap year" : "not a leap year"}.
          </p>
          <p>
            Adjustment:{" "}
            {details.leapAdjustment ? "-1 applied" : "no change"}
          </p>
        </div>
      </details>

      <section className="worked-example">
        <h2>Worked example (live)</h2>
        <div className="example-grid">
          <div>
            <span>Century</span>
            <strong>{details.century}</strong>
          </div>
          <div>
            <span>Anchor</span>
            <strong>{details.anchor}</strong>
          </div>
          <div>
            <span>Decade</span>
            <strong>{details.decade}</strong>
          </div>
          <div>
            <span>Mod</span>
            <strong>{details.d4}</strong>
          </div>
          <div>
            <span>Doomsday</span>
            <strong>{details.doomsday}</strong>
          </div>
          <div>
            <span>Offset</span>
            <strong>{details.offset}</strong>
          </div>
          <div>
            <span>Leap adj.</span>
            <strong>{details.leapAdjustment ? "-1" : "0"}</strong>
          </div>
          <div>
            <span>Final index</span>
            <strong>{details.weekdayIndex}</strong>
          </div>
          <div>
            <span>Weekday</span>
            <strong>{details.weekdayName}</strong>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Method;
